import { useGLTF } from "@react-three/drei";
import { useConfigurationOptionsContext } from "../../../../../app/providers/DoorOptionsProvider";
import { isUHandleCode } from "../../../../../app/providers/doorHandleProfiles";
import { getFullAssetPath } from "../../../../../shared/utils/assets";
import { DoorComponentProps } from "../../Door";
import Beam from "../components/Beam";
import Frame from "../components/Frame";
import Glass from "../components/Glass";
import JHandle from "../components/JHandle";
import Kozijn from "../components/Kozijn";
import Lock from "../components/Lock";
import UHandleFront from "../components/UHandleFront";
import type { OpdekGLTFResult } from "../opdek/OpdekType";
import { DeMaasLayout } from "../layouts/DeMaasLayout";
import { DeWaalCutLayouts } from "../layouts/DeWaalCutLayouts";
import { OpdekVerticalLayout } from "../layouts/OpdekVerticalLayout";

interface OpdekDeurProps extends DoorComponentProps {}

const StompeDeur = ({
  materials,
  position,
  index = 0,
  kozijnSettings,
}: OpdekDeurProps) => {
  const { nodes } = useGLTF(
    getFullAssetPath("/objects-v3/opdekdeur_v6.glb"),
  ) as OpdekGLTFResult;

  const {
    handle,
    getConfigurationBeams,
    getConfigurationWidth,
    getConfigurationHeight,
    getConfigurationColor,
    sceneStaanderPosition,
    isDeWaalLayout,
    isDeMaasLayout,
  } = useConfigurationOptionsContext();

  //Chosen material
  const chosenMaterial = materials[getConfigurationColor()];
  const isVerticalHandle = isUHandleCode(handle?.code);
  const isTaatsHandle = !isVerticalHandle && !!handle?.code.includes("taats");
  const doorWidth = getConfigurationWidth(index);
  const doorHeight = getConfigurationHeight(index);
  const configuredBeams = getConfigurationBeams(isTaatsHandle);

  return (
    <>
      <group dispose={null} position={position}>
        <Kozijn
          doorNodes={nodes}
          materials={materials}
          index={index}
          existingFrame={true}
          {...kozijnSettings}
        />
      </group>
      <group
        dispose={null}
        position={[position.x, position.y, position.z + 0.01]}
      >
        <group rotation-y={Math.PI}>
          <OpdekVerticalLayout
            nodes={nodes}
            material={chosenMaterial}
            width={doorWidth}
            height={doorHeight}
            semanticPosition={sceneStaanderPosition}
          />

          {/* De Waal */}
          {isDeWaalLayout && (
            <DeWaalCutLayouts
              nodes={nodes}
              chosenMaterial={chosenMaterial}
              doorIndex={index}
            />
          )}

          {/* De Maas */}
          {isDeMaasLayout && (
            <DeMaasLayout
              nodes={nodes}
              chosenMaterial={chosenMaterial}
              doorIndex={index}
            />
          )}
        </group>

        <Frame doorNodes={nodes} materials={materials} index={index} />
        <Glass doorNodes={nodes} materials={materials} index={index} />
        {isVerticalHandle ? (
          <>
            <UHandleFront
              doorNodes={nodes}
              materials={materials}
              index={index}
            />
            {configuredBeams.map((beam, i) => {
              return (
                <Beam
                  id={beam.id}
                  key={`stompe-beam-${i}`}
                  doorNodes={nodes}
                  materials={materials}
                  width={doorWidth}
                  height={beam.height / 1000}
                />
              );
            })}
          </>
        ) : isTaatsHandle ? (
          <>
            <JHandle doorNodes={nodes} materials={materials} index={index} />
            {configuredBeams.map((beam, i) => {
              return (
                <Beam
                  id={beam.id}
                  key={`stompe-beam-${i}`}
                  doorNodes={nodes}
                  materials={materials}
                  width={doorWidth}
                  height={beam.height / 1000}
                />
              );
            })}
          </>
        ) : (
          <>
            <Lock
              doorNodes={nodes}
              materials={materials}
              index={index}
              reverse={true}
              type={handle ? handle.code : ""}
            />
            {configuredBeams.map((beam, i) => {
              return (
                <Beam
                  id={beam.id}
                  key={`stompe-beam-${i}`}
                  doorNodes={nodes}
                  materials={materials}
                  width={doorWidth}
                  height={beam.height / 1000}
                />
              );
            })}
          </>
        )}
      </group>
    </>
  );
};
export default StompeDeur;
